"use client";

import { c as _c } from "react/compiler-runtime";
import { ChangeEvent, Suspense, useState } from "react";
import { useRouter } from "next/navigation";
import { chakra, Input, InputGroup } from "@chakra-ui/react";
import { IoSearchSharp } from "react-icons/io5";
import { RxCross2 } from "react-icons/rx";
import DynamicAddressAutofill, { AddressAutofillOptions, AddressAutofillRetrieveResponse } from "./address-autofill";
import { AddressAutofillSuggestionResponse } from "@mapbox/search-js-core";
import { Tooltip } from "./ui/tooltip";
const autofillOptions: AddressAutofillOptions = {
  country: "US",
  limit: 10,
  bbox: [-122.55, 37.69, -122.35, 37.83],
  proximity: {
    lng: -122.4194,
    lat: 37.7749
  },
  streets: false,
  language: "en"
};

// NOTE: UI changes to this page ought to be reflected in its suspense skeleton `search-bar-skeleton.tsx` and vice versa
// TODO: isolate the usage of `useSearchParams()` so that the Suspense boundary can be even more narrow if possible
interface SearchBarProps {
  inputAddress: string;
  onInputAddressChange: (address: string) => void;
  onSearchChange: (coords: number[], address: string) => void;
}
const SearchBar = t0 => {
  const $ = _c(33);
  const {
    inputAddress,
    onInputAddressChange,
    onSearchChange
  } = t0;
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [suggestionCount, setSuggestionCount] = useState(null);
  const router = useRouter();
  const isBelowAutocompleteMinimum = inputAddress.length < 3;
  const searchHint = isBelowAutocompleteMinimum ? "Keep typing \u2014 enter at least 3 characters." : "Try refining your search.";
  const showSearchHint = Boolean(inputAddress.length && !suggestionSelected && (isBelowAutocompleteMinimum || suggestionCount === 0));
  let t1;
  if ($[0] !== onInputAddressChange || $[1] !== router) {
    t1 = () => {
      onInputAddressChange("");
      setSuggestionSelected(false);
      setSuggestionCount(null);
      router.push("/", {
        scroll: false
      });
    };
    $[0] = onInputAddressChange;
    $[1] = router;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleClearClick = t1;
  let t2;
  if ($[3] !== onSearchChange) {
    t2 = res => {
      const addressData = res.features[0];
      const addressLine = res.features[0].properties.feature_name;
      const coords = addressData.geometry.coordinates;
      onSearchChange(coords, addressLine);
      setSuggestionSelected(true);
    };
    $[3] = onSearchChange;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const handleRetrieve = t2;
  let t3;
  if ($[5] !== onInputAddressChange || $[6] !== suggestionSelected) {
    t3 = event => {
      const nextInputAddress = event.currentTarget.value;
      onInputAddressChange(nextInputAddress);
      setSuggestionCount(null);
      if (suggestionSelected && nextInputAddress.length <= 3) {
        setSuggestionSelected(false);
      }
    };
    $[5] = onInputAddressChange;
    $[6] = suggestionSelected;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleAddressChange = t3;
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = res_0 => {
      setSuggestionCount(res_0.suggestions.length);
    };
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleSuggest = t4;
  const handleSubmit = _temp;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      placement: "bottom-start",
      offset: {
        mainAxis: -12
      }
    };
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      base: "full",
      sm: "xs",
      md: "sm"
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <IoSearchSharp color="grey.900" fontSize="1.1em" size="20" data-testid="search-icon" />;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== handleClearClick || $[13] !== inputAddress.length) {
    t8 = inputAddress.length !== 0 && <RxCross2 color="grey.900" fontSize="1.1em" size="20" data-testid="clear-icon" onClick={handleClearClick} />;
    $[12] = handleClearClick;
    $[13] = inputAddress.length;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      base: "lg",
      md: "xl",
      xl: "xl"
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      base: "9",
      md: "12"
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      borderColor: "yellow"
    };
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  let t13;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      borderColor: "yellow",
      _placeholder: {
        color: "grey.900"
      }
    };
    t13 = {
      borderColor: "red"
    };
    $[18] = t12;
    $[19] = t13;
  } else {
    t12 = $[18];
    t13 = $[19];
  }
  let t14;
  if ($[20] !== handleAddressChange || $[21] !== inputAddress) {
    t14 = <Input autoFocus={true} placeholder="Search San Francisco address" fontFamily="body" fontSize="md" size={t9} pt="0" pr="2.5" pb="0" pl={t10} borderRadius="full" border="search" bgColor="white" shadow="search" type="text" name="address-1" value={inputAddress} onChange={handleAddressChange} _focus={t11} _hover={t12} _invalid={t13} autoComplete="address-line1" />;
    $[20] = handleAddressChange;
    $[21] = inputAddress;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t8) {
    t15 = <InputGroup w={t6} data-testid="search-bar" startElement={t7} endElement={t8}>{t14}</InputGroup>;
    $[23] = t14;
    $[24] = t8;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== searchHint || $[27] !== showSearchHint || $[28] !== t15) {
    t16 = <Tooltip content={searchHint} open={showSearchHint} openDelay={500} closeDelay={200} showArrow={true} positioning={t5}>{t15}</Tooltip>;
    $[26] = searchHint;
    $[27] = showSearchHint;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== handleRetrieve || $[31] !== t16) {
    t17 = <chakra.form position="relative" onSubmit={handleSubmit}><Suspense><DynamicAddressAutofill accessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN ?? ""} options={autofillOptions} onRetrieve={handleRetrieve} onSuggest={handleSuggest}>{t16}</DynamicAddressAutofill></Suspense></chakra.form>;
    $[30] = handleRetrieve;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
export default SearchBar;
function _temp(e) {
  e.preventDefault();
}